import React, { useEffect, useState } from 'react';
import { aiAgentApi } from '../lib/aiAgentApi';
import { clearAuth, canSeeAiManager } from '../lib/auth';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { LayoutDashboard, Mail, Activity, ListChecks, Heart, RotateCcw, ArrowLeft, LogOut, Gauge, Shield, Sparkles, ChevronRight, Building2, Users, Search, Copy, Check, Eye } from 'lucide-react';
import { AgentListTab } from '../components/ai_manager/AgentListTab';

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const fmtPct = (n) => `${(n * 100).toFixed(1)}%`;

// Static Tailwind class maps — Tailwind needs to see exact class strings at
// build time, so dynamic `bg-${color}-100` would not work.
const ACCENT_CLASSES = {
  slate:    { border: 'border-slate-100',    text: 'text-slate-700',    bg: 'bg-slate-100',    chip: 'bg-slate-100 text-slate-700' },
  emerald:  { border: 'border-emerald-100',  text: 'text-emerald-700',  bg: 'bg-emerald-100',  chip: 'bg-emerald-100 text-emerald-700' },
  sky:      { border: 'border-sky-100',      text: 'text-sky-700',      bg: 'bg-sky-100',      chip: 'bg-sky-100 text-sky-700' },
  violet:   { border: 'border-violet-100',   text: 'text-violet-700',   bg: 'bg-violet-100',   chip: 'bg-violet-100 text-violet-700' },
  amber:    { border: 'border-amber-100',    text: 'text-amber-700',    bg: 'bg-amber-100',    chip: 'bg-amber-100 text-amber-700' },
  rose:     { border: 'border-rose-100',     text: 'text-rose-700',     bg: 'bg-rose-100',     chip: 'bg-rose-100 text-rose-700' },
  blue:     { border: 'border-blue-100',     text: 'text-blue-700',     bg: 'bg-blue-100',     chip: 'bg-blue-100 text-blue-700' },
  teal:     { border: 'border-teal-100',     text: 'text-teal-700',     bg: 'bg-teal-100',     chip: 'bg-teal-100 text-teal-700' },
  fuchsia:  { border: 'border-fuchsia-100',  text: 'text-fuchsia-700',  bg: 'bg-fuchsia-100',  chip: 'bg-fuchsia-100 text-fuchsia-700' },
};
const acc = (k) => ACCENT_CLASSES[k] || ACCENT_CLASSES.slate;

const TABS = [
  { id: 'overview',     label: 'Overview',       Icon: LayoutDashboard },
  { id: 'agents',       label: 'Agents',         Icon: Users },
  { id: 'email-queue',  label: 'Email Queue',    Icon: Mail },
  { id: 'jobs',         label: 'Jobs',           Icon: ListChecks },
  { id: 'invocations',  label: 'Invocations',    Icon: Activity },
  { id: 'service-health', label: 'Service Health', Icon: Heart },
];

const KpiCard = ({ label, value, accent = 'slate', sub = null }) => {
  const a = acc(accent);
  return (
    <div className={`rounded-xl border ${a.border} bg-white p-4 shadow-sm`} data-testid={`ai-mgr-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${a.text}`}>{value}</div>
      {sub ? <div className="mt-0.5 text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
};

const CategoryCard = ({ cat, meta }) => {
  const a = acc(cat.color);
  const showsTools = meta?.tools?.length || 0;
  return (
    <div className={`rounded-xl border ${a.border} bg-white p-4 shadow-sm`} data-testid={`ai-mgr-cat-${cat.key}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{cat.label}</div>
          <div className={`mt-1 text-xl font-bold ${a.text}`}>{cat.calls.toLocaleString()}</div>
          <div className="mt-0.5 text-xs text-slate-500">
            {cat.tool_count > 0 ? `${cat.tool_count} tool${cat.tool_count === 1 ? '' : 's'} · ` : ''}
            {cat.calls > 0 ? `${fmtPct(cat.success_rate)} success` : 'No calls yet'}
          </div>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${a.chip}`}>{cat.key}</span>
      </div>
      {showsTools > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {meta.tools.map((t) => (
            <span key={t} className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{t}</span>
          ))}
        </div>
      )}
    </div>
  );
};

const OverviewTab = ({ orgId }) => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    aiAgentApi.adminDashboard(days, orgId)
      .then(setData)
      .catch(() => toast.error('Could not load dashboard'))
      .finally(() => setLoading(false));
  }, [days, orgId]);

  if (loading) return <div className="py-12 text-center text-sm text-slate-400">Loading…</div>;
  if (!data) return null;

  const k = data.kpis || {};
  const metaByKey = (data.categories_meta || []).reduce((m, c) => { m[c.key] = c; return m; }, {});
  const totalQt = (data.by_query_type || []).reduce((acc, q) => acc + (q.count || 0), 0);
  const maxQt = Math.max(...(data.by_query_type || []).map(q => q.count || 0), 1);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Activity overview</h2>
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-0.5">
          {[1, 7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`ai-mgr-range-${d}`}
              onClick={() => setDays(d)}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${days === d ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Interactions" value={k.total_interactions?.toLocaleString() || 0} accent="emerald" sub="Assistant replies" />
        <KpiCard label="Threads touched" value={k.total_threads?.toLocaleString() || 0} accent="sky" />
        <KpiCard label="Active users" value={k.active_users?.toLocaleString() || 0} accent="violet" />
        <KpiCard label="AI messages" value={k.total_messages?.toLocaleString() || 0} accent="slate" sub="User + assistant + tool" />
        <KpiCard label="Emails sent" value={k.emails_sent?.toLocaleString() || 0} accent="emerald" />
        <KpiCard label="Pending approval" value={k.emails_pending_approval?.toLocaleString() || 0} accent="amber" sub="AI drafted, awaiting user" />
        <KpiCard label="Queued / sending" value={k.emails_queued?.toLocaleString() || 0} accent="sky" />
        <KpiCard label="Failed" value={k.emails_failed?.toLocaleString() || 0} accent="rose" sub="Need retry" />
      </div>

      {/* By Query Type */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ai-mgr-by-query-type">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900">Query types</h3>
          <span className="text-[11px] text-slate-500">{totalQt.toLocaleString()} classified user turns · last {days}d</span>
        </div>
        {totalQt === 0 ? (
          <div className="py-6 text-center text-xs text-slate-400">
            New queries will be tagged automatically. Historical traffic was not backfilled — only new turns appear here.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {(data.by_query_type || []).map((q) => {
              const a = acc(q.color);
              const widthPct = Math.max(2, Math.round((q.count / maxQt) * 100));
              return (
                <li key={q.key} className="flex items-center gap-3" data-testid={`ai-mgr-qt-${q.key}`}>
                  <div className="w-44 shrink-0 truncate text-xs font-semibold text-slate-700">{q.label}</div>
                  <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden">
                    {q.count > 0 && <div className={`h-full ${a.bg}`} style={{ width: `${widthPct}%` }} />}
                  </div>
                  <div className="w-12 text-right text-xs font-semibold text-slate-600">{q.count.toLocaleString()}</div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* By Organization */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ai-mgr-by-org">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900">By organisation</h3>
          <span className="text-[11px] text-slate-500">
            Top {Math.min(20, (data.by_org || []).length)} organisations by interactions · last {days}d
          </span>
        </div>
        {(data.by_org || []).length === 0 ? (
          <div className="py-6 text-center text-xs text-slate-400">No tagged org traffic yet for this window.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr>
                  <th className="text-left py-1.5">Organisation</th>
                  <th className="text-right py-1.5">Interactions</th>
                  <th className="text-right py-1.5">Active users</th>
                  <th className="text-right py-1.5">Emails sent</th>
                  <th className="text-right py-1.5">Pending approval</th>
                </tr>
              </thead>
              <tbody>
                {(data.by_org || []).map((o) => (
                  <tr key={o.org_id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="py-1.5"><span className="inline-flex items-center gap-1.5"><Building2 className="h-3 w-3 text-slate-400" />{o.org_name}</span></td>
                    <td className="py-1.5 text-right font-semibold">{o.interactions.toLocaleString()}</td>
                    <td className="py-1.5 text-right">{o.active_users.toLocaleString()}</td>
                    <td className="py-1.5 text-right">{o.emails_sent.toLocaleString()}</td>
                    <td className="py-1.5 text-right">{o.emails_pending > 0 ? <span className="rounded-full bg-amber-100 px-1.5 text-amber-700">{o.emails_pending}</span> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-2">Service categories</h3>
        <p className="mb-3 text-xs text-slate-500">
          Every tool the AI Agent can call is grouped by category. New tools automatically appear here.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(data.category_breakdown || []).map((c) => <CategoryCard key={c.key} cat={c} meta={metaByKey[c.key]} />)}
        </div>
      </div>

      {(data.timeline || []).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Interactions per day</h3>
          <div className="flex items-end gap-1.5 h-24">
            {data.timeline.map((row) => {
              const max = Math.max(...data.timeline.map(r => r.interactions || 0), 1);
              const h = Math.max(4, Math.round((row.interactions / max) * 96));
              return (
                <div key={row.date} className="flex-1 flex flex-col items-center gap-1" title={`${row.date}: ${row.interactions}`}>
                  <div className="w-full rounded-t bg-emerald-500" style={{ height: h }} />
                  <div className="text-[9px] text-slate-400">{row.date?.slice(5)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const EmailQueueTab = ({ orgId }) => {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('');
  const [direction, setDirection] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    aiAgentApi.adminEmailQueue({ status: status || undefined, direction: direction || undefined, org_id: orgId || undefined })
      .then(d => setRows(d.emails || []))
      .catch(() => toast.error('Could not load email queue'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, direction, orgId]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1 text-xs">
          <option value="">All statuses</option>
          {['draft', 'pending_approval', 'queued', 'sending', 'sent', 'failed', 'received'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={direction} onChange={(e) => setDirection(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1 text-xs">
          <option value="">In + Out</option>
          <option value="inbound">Inbound</option>
          <option value="outbound">Outbound</option>
        </select>
        <button onClick={load} className="ml-auto rounded-md border border-slate-200 px-2 py-1 text-xs hover:bg-slate-100"><RotateCcw className="inline h-3 w-3 mr-1" />Refresh</button>
      </div>
      {loading ? <div className="py-8 text-center text-sm text-slate-400">Loading…</div> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Dir</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">User</th><th className="text-left px-3 py-2">Subject</th><th className="text-left px-3 py-2">To</th><th className="text-left px-3 py-2">Batch</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-1.5 text-slate-500">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-1.5">{r.direction}</td>
                  <td className="px-3 py-1.5"><span className="rounded-full bg-slate-100 px-1.5 py-0.5 font-semibold">{r.status}</span>{r.ai_drafted && <span className="ml-1 rounded-full bg-violet-100 px-1.5 py-0.5 text-violet-700">AI</span>}</td>
                  <td className="px-3 py-1.5 truncate max-w-[180px]">{r.user_email || r.user_id}</td>
                  <td className="px-3 py-1.5 truncate max-w-[300px]">{r.subject}</td>
                  <td className="px-3 py-1.5 truncate max-w-[200px]">{(r.to || []).join(', ')}</td>
                  <td className="px-3 py-1.5 text-slate-500">{r.batch_id ? <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">{r.batch_id}</span> : '—'}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No emails match.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const JobsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    aiAgentApi.adminJobs().then(d => setRows(d.jobs || [])).catch(() => toast.error('Could not load jobs')).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="py-8 text-center text-sm text-slate-400">Loading…</div>;
  if (rows.length === 0) return <div className="py-8 text-center text-sm text-slate-400">No long-running jobs yet. (Future agent jobs will surface here.)</div>;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full text-xs">
        <thead className="bg-slate-50"><tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Tool</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Progress</th><th className="text-left px-3 py-2">User</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100">
              <td className="px-3 py-1.5">{fmtDate(r.created_at)}</td>
              <td className="px-3 py-1.5">{r.tool_name}</td>
              <td className="px-3 py-1.5"><span className="rounded-full bg-slate-100 px-1.5 py-0.5 font-semibold">{r.status}</span></td>
              <td className="px-3 py-1.5">{r.progress ?? 0}%</td>
              <td className="px-3 py-1.5">{r.user_id}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const InvocationsTab = ({ orgId }) => {
  const [rows, setRows] = useState([]);
  const [tool, setTool] = useState('');
  const [status, setStatus] = useState('');
  const [queryType, setQueryType] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    aiAgentApi.adminInvocations({
      tool: tool || undefined,
      status: status || undefined,
      org_id: orgId || undefined,
      query_type: queryType || undefined,
    })
      .then(d => setRows(d.invocations || []))
      .catch(() => toast.error('Could not load invocations'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tool, status, orgId, queryType]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input value={tool} onChange={(e) => setTool(e.target.value)} placeholder="Filter by tool name…" className="rounded-md border border-slate-200 px-2 py-1 text-xs" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1 text-xs">
          <option value="">All statuses</option>
          {['ok', 'error', 'denied_out_of_scope', 'invalid_spec', 'tool_exception'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={queryType} onChange={(e) => setQueryType(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1 text-xs">
          <option value="">All query types</option>
          {['vendor_outreach', 'payment_extensions', 'missing_bills', 'overdues', 'bills', 'payments', 'reporting', 'investigations', 'navigation', 'notes_tasks', 'other'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <button onClick={load} className="ml-auto rounded-md border border-slate-200 px-2 py-1 text-xs hover:bg-slate-100"><RotateCcw className="inline h-3 w-3 mr-1" />Refresh</button>
      </div>
      {loading ? <div className="py-8 text-center text-sm text-slate-400">Loading…</div> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-slate-50"><tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Tool</th><th className="text-left px-3 py-2">Category</th><th className="text-left px-3 py-2">Query type</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">User</th><th className="text-left px-3 py-2">Rows</th><th className="text-left px-3 py-2">Latency</th><th className="text-left px-3 py-2">Error</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 text-slate-500">{fmtDate(r.ts)}</td>
                  <td className="px-3 py-1.5 font-mono">{r.tool}</td>
                  <td className="px-3 py-1.5 text-slate-500">{r.category || '—'}</td>
                  <td className="px-3 py-1.5 text-slate-500">{r.query_type ? <span className="rounded-full bg-slate-100 px-1.5 text-slate-700">{r.query_type}</span> : '—'}</td>
                  <td className="px-3 py-1.5"><span className={`rounded-full px-1.5 py-0.5 font-semibold ${r.status === 'ok' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{r.status}</span></td>
                  <td className="px-3 py-1.5 truncate max-w-[160px]">{r.user_email || r.user_id}</td>
                  <td className="px-3 py-1.5">{r.row_count ?? '—'}</td>
                  <td className="px-3 py-1.5">{r.latency_ms ? `${r.latency_ms}ms` : '—'}</td>
                  <td className="px-3 py-1.5 truncate max-w-[260px] text-rose-700">{r.error || ''}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">No invocations match.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ServiceHealthTab = ({ orgId }) => {
  const [days, setDays] = useState(7);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    aiAgentApi.adminServiceHealth(days, orgId).then(d => setRows(d.services || [])).catch(() => toast.error('Could not load')).finally(() => setLoading(false));
  }, [days, orgId]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {[1, 7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} className={`rounded-md px-2 py-1 text-xs font-semibold ${days === d ? 'bg-emerald-600 text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-100'}`}>{d}d</button>
        ))}
      </div>
      {loading ? <div className="py-8 text-center text-sm text-slate-400">Loading…</div> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-slate-50"><tr><th className="text-left px-3 py-2">Tool</th><th className="text-left px-3 py-2">Calls</th><th className="text-left px-3 py-2">Success</th><th className="text-left px-3 py-2">Success rate</th><th className="text-left px-3 py-2">Avg latency</th><th className="text-left px-3 py-2">Max latency</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.tool} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 font-mono">{r.tool}</td>
                  <td className="px-3 py-1.5">{r.calls.toLocaleString()}</td>
                  <td className="px-3 py-1.5">{r.success.toLocaleString()}</td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-24 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full ${r.success_rate >= 0.95 ? 'bg-emerald-500' : r.success_rate >= 0.8 ? 'bg-amber-400' : 'bg-rose-500'}`} style={{ width: `${(r.success_rate * 100).toFixed(0)}%` }} />
                      </div>
                      <span>{fmtPct(r.success_rate)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-1.5">{r.avg_latency_ms}ms</td>
                  <td className="px-3 py-1.5">{r.max_latency_ms}ms</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No data yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function AIManagerPage() {
  const location = useLocation();
  const navigate = useNavigate();
  // Tab is encoded in the URL hash: #overview / #email-queue / etc.
  const initialTab = (location.hash || '#overview').replace(/^#/, '');
  const [tab, setTab] = useState(TABS.find(t => t.id === initialTab)?.id || 'overview');
  const [orgId, setOrgId] = useState('');
  const [orgs, setOrgs] = useState([]);

  useEffect(() => {
    const t = (location.hash || '#overview').replace(/^#/, '');
    if (TABS.find(x => x.id === t)) setTab(t);
  }, [location.hash]);

  useEffect(() => {
    aiAgentApi.adminOrgs()
      .then((d) => setOrgs(d.orgs || []))
      .catch(() => { /* silent — dashboard still works */ });
  }, []);

  if (!canSeeAiManager()) return <Navigate to="/dashboard" replace />;

  const onTab = (id) => {
    setTab(id);
    navigate(`${location.pathname}#${id}`, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Super Admin header (matches /super-admin shell) */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6" strokeWidth={2} />
            <h1 className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Super Admin
            </h1>
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
              <Sparkles className="h-3 w-3" /> AI Manager
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost" size="sm" className="rounded-none"
              onClick={() => navigate('/cockpit')}
              data-testid="ai-mgr-go-cockpit"
            >
              <Gauge className="h-4 w-4 mr-2" />
              Cockpit
            </Button>
            <Button
              variant="ghost" size="sm" className="rounded-none"
              onClick={() => navigate('/super-admin')}
              data-testid="ai-mgr-back-superadmin"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Super Admin
            </Button>
            <Button
              variant="ghost" size="sm" className="rounded-none"
              onClick={() => { clearAuth(); window.location.href = '/login'; }}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Sub-tabs nav (matches Super Admin tab styling) */}
      <nav className="border-b border-border bg-card">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-center gap-1">
            {TABS.map(({ id, label, Icon }) => (
              <button
                key={id}
                data-testid={`ai-mgr-tab-${id}`}
                onClick={() => onTab(id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                  tab === id
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-6 md:px-12 py-8">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Super Admin</span>
              <ChevronRight className="h-3 w-3" />
              <span>AI Manager</span>
              <ChevronRight className="h-3 w-3" />
              <span className="text-foreground">{TABS.find(t => t.id === tab)?.label}</span>
            </div>
            <h2 className="mt-1 text-xl font-bold tracking-tight text-foreground">
              {TABS.find(t => t.id === tab)?.label}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Observe what the AI Agent is doing across the platform — every interaction, email, job, and tool call is captured here.
              New services and tools register themselves into the categories you see in Overview.
            </p>
          </div>
          {orgs.length > 0 && (
            <div className="flex items-center gap-2" data-testid="ai-mgr-org-filter-wrap">
              <Building2 className="h-4 w-4 text-slate-400" />
              <select
                data-testid="ai-mgr-org-filter"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700"
              >
                <option value="">All organisations</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {tab === 'overview' && <OverviewTab orgId={orgId || null} />}
        {tab === 'agents' && <AgentListTab orgId={orgId || null} />}
        {tab === 'email-queue' && <EmailQueueTab orgId={orgId || null} />}
        {tab === 'jobs' && <JobsTab />}
        {tab === 'invocations' && <InvocationsTab orgId={orgId || null} />}
        {tab === 'service-health' && <ServiceHealthTab orgId={orgId || null} />}
      </main>
    </div>
  );
}
